import { Injectable, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { TourCoordinator } from './tour-coordinator.service';
import { TourRegistry } from './tour-registry.service';
import { TourFeature } from '../config/tour-feature.enum';
import { TourStep } from '../models/tour-step.model';

/**
 * Forgiving-Tour-Click-Delegator (MVP).
 *
 * Hört globale Document-Clicks während eine Tour aus der `FORGIVING_TOURS`-
 * Allowlist aktiv ist. Wenn der User auf einen Anker klickt, der zu einem
 * anderen Step der aktiven Tour gehört (vorwärts ODER rückwärts), wird die
 * Tour über `coordinator.jumpToStep(idx)` umgesteuert.
 *
 * Architektur (siehe `docs/onboarding-refactor/FORGIVING_TOUR_INVENTORY.md`):
 * - Beim Tour-Start: Anchor → StepIdx-Map aus Tour-Config bauen.
 *   Berücksichtigt: `step.anchor`, `step.spotlightAnchor`,
 *   `step.additionalSpotlightAnchors` — also alle Anker, an denen ein Step
 *   visuell „lebt".
 * - Bei Tour-Ende: Listener entfernen, Map leeren.
 * - 150 ms Debounce gegen Doppel-Klicks (Audio-Glitch-Schutz).
 * - „Klick auf aktuellen Step" wird ignoriert (kein Self-Jump).
 *
 * Aktiviert per Allowlist, damit andere Tours (Dialog-Wizards mit eigenem
 * Field-Following) unangetastet bleiben. Erweiterung der Allowlist ist
 * trivial — `FORGIVING_TOURS` ergänzen, sonst nichts.
 */
@Injectable({ providedIn: 'root' })
export class ForgivingTourClickDelegator implements OnDestroy {
  /**
   * Welche Touren sollen Forgiving-Verhalten haben. Alle Setup-Touren
   * (`category: 'setup'`), die auf einer Page laufen und keinen reinen
   * Dialog-Wizard-Flow haben. Contextual-Help-Touren (`category:
   * 'contextual-help'`) bleiben ausgeklammert — die haben einen eigenen
   * On-demand-Mechanismus via `HelpModeService`.
   *
   * Schutz gegen Dialog-Step-Konflikte ist in `maybeJumpTo()` umgesetzt:
   * sobald der aktuell aktive Step `dialogContext.attachToDialog: true`
   * trägt, ignoriert der Delegator Klicks — das bestehende dialog-scoped
   * Field-Following im `TourCoordinator` macht den Job dort.
   */
  private readonly FORGIVING_TOURS: ReadonlySet<TourFeature> = new Set([
    TourFeature.CompanyInfo,
    TourFeature.FirstDepartment,
    TourFeature.AddSkills,
    TourFeature.FirstEmployee,
    TourFeature.TimeRecording,
  ]);

  private readonly DEBOUNCE_MS = 150;

  private listener: ((e: MouseEvent) => void) | null = null;
  private anchorToStepIdx = new Map<string, number>();
  private activeTourId: TourFeature | null = null;
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private sub: Subscription | null = null;

  constructor(
    private readonly coordinator: TourCoordinator,
    private readonly registry: TourRegistry
  ) {
    this.sub = this.coordinator.activeTour$.subscribe((tourId) =>
      this.onActiveTourChange(tourId)
    );
  }

  ngOnDestroy(): void {
    this.detach();
    this.sub?.unsubscribe();
  }

  private onActiveTourChange(tourId: TourFeature | null): void {
    if (tourId && this.FORGIVING_TOURS.has(tourId)) {
      this.attach(tourId);
    } else {
      this.detach();
    }
  }

  private attach(tourId: TourFeature): void {
    this.detach();
    this.activeTourId = tourId;
    this.buildAnchorMap(tourId);
    this.listener = (e) => this.onClick(e);
    document.addEventListener('click', this.listener, { capture: true });
  }

  private detach(): void {
    if (this.listener) {
      document.removeEventListener('click', this.listener, {
        capture: true,
      } as EventListenerOptions);
      this.listener = null;
    }
    if (this.debounceTimer !== null) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    this.anchorToStepIdx.clear();
    this.activeTourId = null;
  }

  private buildAnchorMap(tourId: TourFeature): void {
    this.anchorToStepIdx.clear();
    const config = this.registry.get(tourId);
    if (!config) return;
    const steps = config.chapters.flatMap((c) => c.steps);

    // ───── Pass 1: explizite Step-Anker ─────
    // Direkte Bindungen (anchor / spotlightAnchor / additionalSpotlightAnchors)
    // werden ZUERST eingetragen — über alle Steps hinweg. Damit gewinnen
    // explizite Bindungen immer vor Sub-Anker-Funden des nächsten Pass.
    // Ohne das hat z. B. der Empty-State-„Abteilung hinzufügen"-Button
    // (additionalSpotlightAnchor von Step 2) gegen Step 1 verloren, dessen
    // großer Spotlight `organization-view` den Button als Sub-Kind hatte —
    // Folge: Klick auf Empty-State sprang fälschlich zurück zu Step 1.
    steps.forEach((step, idx) => {
      for (const a of this.collectAnchors(step)) {
        if (!this.anchorToStepIdx.has(a)) {
          this.anchorToStepIdx.set(a, idx);
        }
      }
    });

    // ───── Pass 2: Sub-Anker im Spotlight-Subtree ─────
    // Beispiel: Step 1 hat Spotlight `company-info` (Card mit drei Feldern).
    // Klick auf ein Feld der Card soll als Step-1-Klick gelten — das
    // tragen wir hier nach. Aber nur, wenn der Anker nicht schon in Pass 1
    // einem expliziten Step zugeordnet wurde.
    steps.forEach((step, idx) => {
      const spotlightAnchors = this.collectSpotlightAnchors(step);
      for (const spotlightId of spotlightAnchors) {
        const root = document.querySelector(
          `[onboardingAnchor="${spotlightId}"]`
        );
        if (!root) continue;
        const children = root.querySelectorAll('[onboardingAnchor]');
        for (const child of Array.from(children)) {
          const childId = child.getAttribute('onboardingAnchor');
          if (!childId) continue;
          if (!this.anchorToStepIdx.has(childId)) {
            this.anchorToStepIdx.set(childId, idx);
          }
        }
      }
    });
  }

  /**
   * Nur die Spotlight-Anker eines Steps (ohne den Popover-Anchor selbst).
   * Spotlights sind in der Regel größere Container (Cards/Sections), unter
   * denen Sub-Felder als Forgiving-Targets gelten sollen.
   */
  private collectSpotlightAnchors(step: TourStep): string[] {
    const result: string[] = [];
    if (typeof step.spotlightAnchor === 'string') {
      result.push(step.spotlightAnchor);
    }
    const additional = step.additionalSpotlightAnchors;
    if (Array.isArray(additional)) {
      for (const a of additional) {
        if (typeof a === 'string') result.push(a);
      }
    }
    return result;
  }

  private collectAnchors(step: TourStep): string[] {
    const result: string[] = [];
    if (typeof step.anchor === 'string') result.push(step.anchor);
    if (typeof step.spotlightAnchor === 'string') {
      result.push(step.spotlightAnchor);
    }
    const additional = step.additionalSpotlightAnchors;
    if (Array.isArray(additional)) {
      for (const a of additional) {
        if (typeof a === 'string') result.push(a);
      }
    }
    return result;
  }

  private onClick(e: MouseEvent): void {
    const tourId = this.activeTourId;
    if (!tourId) return;
    const target = e.target as Element | null;
    if (!target) return;

    // Parent-Traversal: das geklickte Element kann ein Kind eines
    // [onboardingAnchor]-Containers sein (z. B. ein Label/Icon innerhalb
    // eines Feldes). Wir steigen die DOM-Hierarchie hoch, bis wir einen
    // Anker finden, der in unserer Map ist UND der nicht zu groß ist.
    //
    // Größen-Filter: Page-Container-Anker (z. B. `organization-view` mit
    // ~1400×700 px = die ganze Page) sind keine gezielten Klick-Targets.
    // Klick auf eine leere Fläche der Page würde sonst die Tour zu dem
    // Step mit diesem Container-Anker werfen (typischerweise Step 1,
    // Welcome-Overlay). Stattdessen weiter parent-traversen — und wenn
    // kein kleinerer Anker mehr kommt: ignorieren.
    const VIEWPORT_W = window.innerWidth;
    const VIEWPORT_H = window.innerHeight;
    let cursor: Element | null = target;
    let foundAnchor: string | null = null;
    while (cursor && cursor !== document.body) {
      const a =
        cursor.getAttribute?.('onboardingAnchor') ||
        cursor.getAttribute?.('anchor');
      if (a && this.anchorToStepIdx.has(a)) {
        const r = cursor.getBoundingClientRect();
        const isPageContainer =
          r.width > VIEWPORT_W * 0.6 && r.height > VIEWPORT_H * 0.4;
        if (!isPageContainer) {
          foundAnchor = a;
          break;
        }
        // Container ist Page-groß → ignorieren, weiter hochsuchen.
      }
      cursor = cursor.parentElement;
    }
    if (!foundAnchor) return;

    let targetStepIdx = this.anchorToStepIdx.get(foundAnchor)!;
    const currentStepId = this.coordinator.currentActiveStepId;
    const config = this.registry.get(tourId);
    if (!config) return;
    const steps = config.chapters.flatMap((c) => c.steps);
    const currentIdx = steps.findIndex((s) => s.id === currentStepId);

    // Same step → kein Jump (würde nur Voice neu starten).
    if (currentIdx === targetStepIdx) return;
    // Kein aktiver Step (Tour gerade im Übergang) → ignorieren.
    if (currentIdx < 0) return;

    // Mehrere Steps können denselben Anchor verwenden (z. B. ein Page-
    // Anchor wie `skills-table` ist in Skills-Tour sowohl Step 2 als
    // auch dem Outro Step 7 zugeordnet). Die `anchorToStepIdx`-Map ist
    // First-Wins über alle Steps — sie würde den Klick zum FRÜHESTEN
    // Step routen (typisch ein Click-Action-Step), auch wenn der User
    // gerade auf einem späteren Step mit demselben Anker steht.
    //
    // Wenn der aktuelle Step diesen Anchor ebenfalls verwendet, ist der
    // Klick im Kontext des aktuellen Steps — kein Jump. Ohne dies würde
    // der Klick z. B. im Outro die Tour fälschlich auf Step 2 zurück-
    // werfen (mit Cancel+Restart + Spotlight-Verlust).
    const currentStep = steps[currentIdx];
    if (currentStep && this.collectAnchors(currentStep).includes(foundAnchor)) {
      return;
    }

    // Dialog-Schutz: wenn der aktuelle Step im Modal-Dialog rendert
    // (attachToDialog: true), zieht sich Forgiving komplett zurück. Im
    // Dialog ist die Tour über das Dialog-scoped Field-Following des
    // TourCoordinators (Z. ~2087-2225) ausreichend bedient.
    //
    // Bewusst NICHT versucht: „Click-Action im Dialog → springe zu Step+1
    // (Outro)". Das macht intern Cancel+Restart der Tour, was mit dem
    // gleichzeitig laufenden Dialog-Close, Form-Submit-Callbacks und
    // DOM-Mutationen race-anfällig ist — Tour-State wurde inkonsistent
    // (off-by-one in completedStepCount). Der saubere Pfad für „Step
    // skippt im Dialog auf Outro" liegt in tour-spezifischer Config
    // (advanceOnDocumentEvent + skipIf), nicht im Forgiving-Delegator.
    if (currentStep?.dialogContext?.attachToDialog) return;

    // „Klick = Vollzug"-Regel (außerhalb Dialog): wenn der Ziel-Step
    // eine reine Click-Action ist (Button-Klick öffnet Dialog,
    // navigiert, triggert App-Event), hat der Klick den Step bereits
    // vollzogen — Tour springt direkt zu Step+1.
    const targetStep = steps[targetStepIdx];
    const isClickAction =
      !!targetStep &&
      (targetStep.nextCondition === 'onClick' ||
        !!targetStep.advanceOnDocumentEvent);
    if (isClickAction && targetStepIdx + 1 < steps.length) {
      targetStepIdx = targetStepIdx + 1;
    }

    // Debounce: schnell aufeinander folgende Klicks (z. B. Doppelklick auf
    // Dropdown) sollen nur einen einzigen Jump auslösen.
    if (this.debounceTimer !== null) clearTimeout(this.debounceTimer);
    const finalTargetIdx = targetStepIdx;
    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null;
      this.coordinator.jumpToStep(tourId, finalTargetIdx).catch(() => {
        /* Promise-Rejects vom Coordinator (Cancel, AbortSignal) sind
           im Forgiving-Flow erwartbar — Tour wird neu gestartet und
           der alte Render-Loop wirft seinen Abort. Kein Logging. */
      });
    }, this.DEBOUNCE_MS);
  }
}
